import React, { useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";

interface VehicleItem {
  _id: string;
  inDate: string;
  outDate: string;
  driverName: string;
  licenseNumber: string;
  category: string;
}

interface VehicleTabProps {
  datas: VehicleItem[];
  hasMore: boolean;
  handleLoadMore: () => void;
  onFilter: (filters: any) => void;
  onReset: () => void;
}

// Vehicle Filter Component
const VehicleFilter = ({ onFilter, onReset, companyColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    startInDate: "",
    endInDate: "",
    startOutDate: "",
    endOutDate: "",
    category: "",
    categoryOwner: "",
    name: "",
    licenseNumber: "",
  });

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const formatDateTimeForAPI = (
    dateString: string,
    isEndDate: boolean = false
  ) => {
    if (!dateString) return "";
    // Untuk end date, set waktu ke 23:59:59
    // Untuk start date, set waktu ke 00:00:00
    const time = isEndDate ? "23:59:59" : "00:00:00";
    return moment(dateString).format(`YYYY-MM-DD ${time}`);
  };

  const handleApply = () => {
    // Format semua tanggal sebelum mengirim
    const formattedFilters = {
      ...filters,
      startInDate: formatDateTimeForAPI(filters.startInDate, false),
      endInDate: formatDateTimeForAPI(filters.endInDate, true),
      startOutDate: formatDateTimeForAPI(filters.startOutDate, false),
      endOutDate: formatDateTimeForAPI(filters.endOutDate, true),
    };
    onFilter(formattedFilters);
    setIsOpen(false);
  };

  const handleReset = () => {
    const resetFilters = {
      startInDate: "",
      endInDate: "",
      startOutDate: "",
      endOutDate: "",
      category: "",
      categoryOwner: "",
      name: "",
      licenseNumber: "",
    };
    setFilters(resetFilters);
    onReset();
    setIsOpen(false);
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white rounded-lg shadow px-4 py-2 flex items-center gap-2 w-full justify-between hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon="filter" className="text-gray-600" />
          <span className="font-medium text-gray-700">Filter</span>
        </div>
        <FontAwesomeIcon
          icon={isOpen ? "chevron-up" : "chevron-down"}
          className="text-gray-600"
        />
      </button>

      {isOpen && (
        <div className="bg-white rounded-lg shadow mt-2 p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tanggal Masuk
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={filters.startInDate}
                onChange={(e) => handleChange("startInDate", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="date"
                value={filters.endInDate}
                onChange={(e) => handleChange("endInDate", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tanggal Keluar
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={filters.startOutDate}
                onChange={(e) => handleChange("startOutDate", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="date"
                value={filters.endOutDate}
                onChange={(e) => handleChange("endOutDate", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori Kendaraan
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleChange("category", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Semua</option>
              <option value="MOTOR">MOTOR</option>
              <option value="MOBIL">MOBIL</option>
              <option value="TRUK">TRUK</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori Pemilik Kendaraan
            </label>
            <select
              value={filters.categoryOwner}
              onChange={(e) => handleChange("categoryOwner", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Semua</option>
              <option value="TAMU">TAMU</option>
              <option value="KARYAWAN">KARYAWAN</option>
              <option value="VENDOR">VENDOR</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nama Pengemudi
            </label>
            <input
              type="text"
              value={filters.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Masukkan nama pengemudi"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plat Nomor Kendaraan
            </label>
            <input
              type="text"
              value={filters.licenseNumber}
              onChange={(e) => handleChange("licenseNumber", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Masukkan plat nomor"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              style={{ backgroundColor: companyColor || "#9333ea" }}
              className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              Terapkan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const VehicleTab: React.FC<VehicleTabProps> = ({
  datas,
  hasMore,
  handleLoadMore,
  onFilter,
  onReset,
}) => {
  const navigate = useNavigate();
  const [companyProfile] = useState<any>(
    JSON.parse(localStorage.getItem("companyProfile") || "{}")
  );

  const canEdit = (item: VehicleItem) => {
    return !item.outDate;
  };

  return (
    <div className="relative flex-1 overflow-y-auto p-4 space-y-4">
      <VehicleFilter
        onFilter={onFilter}
        onReset={onReset}
        companyColor={companyProfile?.color}
      />

      {datas?.map((item) => (
        <div
          key={item._id}
          className="bg-white rounded-xl shadow p-4 flex justify-between items-start"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-2 text-sm">
                <FontAwesomeIcon
                  icon="arrow-right-to-bracket"
                  className="text-green-600"
                />
                <span className="font-medium text-green-700">Masuk</span>
                <span className="text-gray-700">
                  {moment(item.inDate).format("DD MMM YYYY, HH:mm")}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-sm">
                <FontAwesomeIcon
                  icon="arrow-right-from-bracket"
                  className={item.outDate ? "text-red-600" : "text-gray-400"}
                />
                <span
                  className={`font-medium ${
                    item.outDate ? "text-red-700" : "text-gray-400"
                  }`}
                >
                  Keluar
                </span>
                <span
                  className={item.outDate ? "text-gray-700" : "text-gray-400"}
                >
                  {item.outDate
                    ? moment(item.outDate).format("DD MMM YYYY, HH:mm")
                    : "Belum keluar"}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-sm text-gray-500 flex items-center gap-2 font-light">
                <FontAwesomeIcon icon="user" className="text-gray-500" />
                Pengemudi
              </p>
              <p className="font-semibold text-sm mt-1">{item.driverName}</p>
            </div>

            <div className="mt-2">
              <p className="text-sm text-gray-500 flex items-center gap-2 font-light">
                <FontAwesomeIcon icon="car" className="text-gray-500" />
                Plat Nomor
              </p>
              <p className="font-semibold text-sm mt-1">{item.licenseNumber}</p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            {canEdit(item) && (
              <button
                onClick={() =>
                  navigate(`/home/log-book-list/vehicle/${item._id}`)
                }
                className="text-gray-500 hover:text-purple-600 transition"
                aria-label="Edit"
              >
                <FontAwesomeIcon icon="pencil" />
              </button>
            )}
            <span className="bg-purple-100 text-purple-700 px-3 py-1 text-xs rounded-full">
              {item.category}
            </span>
          </div>
        </div>
      ))}

      {hasMore ? (
        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            className="bg-white rounded-xl shadow p-4 w-full hover:bg-gray-100 text-sm font-light transition"
          >
            Tampilkan lebih banyak
          </button>
        </div>
      ) : (
        <p className="text-center text-gray-500 text-sm font-light">
          Semua data telah ditampilkan
        </p>
      )}

      <button
        style={{ backgroundColor: companyProfile?.color || "#9333ea" }}
        onClick={() => navigate("/home/log-book-list/vehicle/add")}
        className="fixed bottom-6 right-6 text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl shadow-lg hover:opacity-90 transition"
        aria-label="Tambah kendaraan"
      >
        +
      </button>
    </div>
  );
};

export default VehicleTab;
